export interface ParsedVideo {
  /** 'embed' for iframe-based players (YouTube, Vimeo); 'direct' for native <video> src */
  type: 'embed' | 'direct'
  url: string
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov']

/**
 * Decodes common HTML entities that appear in URLs extracted from HTML source
 */
const decodeHtmlEntities = (str: string): string =>
  str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')

/**
 * Checks if the URL pathname (ignoring query params) ends with a video extension
 */
const hasVideoExtension = (pathname: string): boolean =>
  VIDEO_EXTENSIONS.some(ext => pathname.toLowerCase().endsWith(ext))

/**
 * Parses a video URL and returns a structured result indicating how to render it.
 * - YouTube/Vimeo → { type: 'embed', url } for iframe rendering
 * - Direct video files (S3 presigned, CDN, etc.) → { type: 'direct', url } for <video> element
 * @param {string} url - The video URL
 * @returns {ParsedVideo|null} - Parsed result or null if unsupported
 */
export const parseVideoUrl = (url: string): ParsedVideo | null => {
  if (!url) return null

  // Decode HTML entities that may be present if URL was extracted from HTML source
  const cleanUrl = decodeHtmlEntities(url)

  try {
    const urlObj = new URL(cleanUrl)

    // YouTube formats
    if (
      urlObj.hostname.includes('youtube.com') ||
      urlObj.hostname.includes('www.youtube.com')
    ) {
      // Standard watch URL: youtube.com/watch?v=ID
      // Shorts URL: youtube.com/shorts/ID
      // Embed URL: youtube.com/embed/ID
      const videoId =
        urlObj.searchParams.get('v') ??
        urlObj.pathname.match(/^\/(?:shorts|embed|live)\/([^/?]+)/)?.[1] ??
        null
      if (videoId) {
        return {
          type: 'embed',
          url: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`,
        }
      }
    }

    // YouTube short format (youtu.be)
    if (urlObj.hostname === 'youtu.be') {
      const videoId = urlObj.pathname.slice(1)
      if (videoId) {
        return {
          type: 'embed',
          url: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`,
        }
      }
    }

    // Vimeo formats
    if (
      urlObj.hostname.includes('vimeo.com') ||
      urlObj.hostname.includes('www.vimeo.com')
    ) {
      const videoId = urlObj.pathname.split('/')[1]
      if (videoId) {
        return {
          type: 'embed',
          url: `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1`,
        }
      }
    }

    // Direct video files (S3 presigned URLs, CDN-hosted, etc.)
    if (hasVideoExtension(urlObj.pathname)) {
      return { type: 'direct', url: cleanUrl }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Gets the string name of a tab from its numeric index
 * Used for analytics tracking
 * @param {number|null} tabIndex - The tab index
 * @param {Object} dynamicTabs - The dynamic tabs object with actual indices
 * @returns {string} - The tab name string
 */
export const getTabName = (
  tabIndex: number | null,
  dynamicTabs?: {
    NONE: null
    PHOTOS: number
    VIDEO: number
    MAP: number
    STREET: number
  }
): string => {
  if (tabIndex === null) return 'none'

  // If dynamicTabs is provided, use it to create a reverse lookup
  if (dynamicTabs) {
    const reverseLookup: Record<number, string> = {}
    Object.entries(dynamicTabs).forEach(([key, value]) => {
      if (value !== null && value >= 0) {
        reverseLookup[value] = key.toLowerCase()
      }
    })
    return reverseLookup[tabIndex] || 'unknown'
  }

  // Fallback to static mapping (for backwards compatibility)
  // TABS.PHOTOS = 0, TABS.VIDEO = 1, TABS.MAP = 2, TABS.STREET = 3
  const tabNames: Record<number, string> = {
    0: 'photos',
    1: 'video',
    2: 'map',
    3: 'street',
  }

  return tabNames[tabIndex] || 'unknown'
}
